/**
 * Public geocoding service wrapper (Photon autocomplete).
 */

import unauthenticatedApi from "@/utils/unauthenticatedAxios";

const AUTOCOMPLETE_PATH = "/public/geocoding/autocomplete";
const TELEMETRY_PATH = "/public/geocoding/telemetry";

export type GeocodingConfidence = "EXACT" | "PARTIAL" | "AMBIGUOUS";

export type GeocodingRegionType =
  | "STATE"
  | "ADMINISTRATIVE_DISTRICT"
  | "DISTRICT"
  | "POSTAL_CODE";

export type GeocodingRegion = {
  id: string;
  name: string;
  nameEn?: string | null;
  pathIds: string[];
  pathNames: string[];
  pathNamesEn?: string[];
  type: GeocodingRegionType | null;
};

export type GeocodingSuggestion = {
  id: string;
  label: string;
  postalCode: string | null;
  city: string | null;
  state: string | null;
  region: GeocodingRegion | null;
  confidence: GeocodingConfidence;
};

export type GeocodingSearchResult =
  | { ok: true; suggestions: GeocodingSuggestion[] }
  | {
      ok: false;
      kind: "bad_gateway" | "invalid_response" | "network" | "unknown";
      status?: number;
    };

const CACHE_TTL_MS = 120_000;
const cache = new Map<
  string,
  { ts: number; suggestions: GeocodingSuggestion[] }
>();

const normalizeQuery = (query: string) => query.trim().toLowerCase();

const dedupeSuggestions = (
  suggestions: GeocodingSuggestion[]
): GeocodingSuggestion[] => {
  const seenIds = new Set<string>();
  const seen = new Set<string>();
  const result: GeocodingSuggestion[] = [];
  for (const suggestion of suggestions) {
    if (suggestion.id && seenIds.has(suggestion.id)) continue;
    if (suggestion.id) {
      seenIds.add(suggestion.id);
    }
    const key = [
      suggestion.label.trim().toLowerCase(),
      suggestion.postalCode ?? "",
      suggestion.city ?? "",
      suggestion.state ?? "",
    ].join("|");
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(suggestion);
  }
  return result;
};

const normalizeRegion = (raw: any): GeocodingRegion | null => {
  if (!raw || typeof raw !== "object") return null;
  const id = typeof raw.id === "string" ? raw.id : String(raw.id ?? "");
  const name = typeof raw.name === "string" ? raw.name : String(raw.name ?? "");
  const nameEn =
    typeof raw.nameEn === "string" && raw.nameEn.trim() ? raw.nameEn.trim() : null;
  const type =
    typeof raw.type === "string"
      ? (raw.type.toUpperCase() as GeocodingRegionType)
      : null;

  if (!id || !name) return null;
  if (
    type !== null &&
    type !== "STATE" &&
    type !== "ADMINISTRATIVE_DISTRICT" &&
    type !== "DISTRICT" &&
    type !== "POSTAL_CODE"
  ) {
    return null;
  }

  const pathIds = Array.isArray(raw.pathIds)
    ? raw.pathIds.map((value: any) => String(value))
    : [];
  const pathNames = Array.isArray(raw.pathNames)
    ? raw.pathNames.map((value: any) => String(value))
    : [];
  const pathNamesEn = Array.isArray(raw.pathNamesEn)
    ? raw.pathNamesEn.map((value: any) => String(value))
    : [];

  return { id, name, nameEn, pathIds, pathNames, pathNamesEn, type };
};

const normalizeSuggestion = (raw: any): GeocodingSuggestion | null => {
  if (!raw || typeof raw !== "object") return null;
  const id = typeof raw.id === "string" ? raw.id : String(raw.id ?? "");
  const label =
    typeof raw.label === "string" ? raw.label : String(raw.label ?? "");
  const confidence = String(raw.confidence ?? "").toUpperCase();

  if (!id || !label) return null;
  if (
    confidence !== "EXACT" &&
    confidence !== "PARTIAL" &&
    confidence !== "AMBIGUOUS"
  ) {
    return null;
  }

  return {
    id,
    label,
    postalCode:
      typeof raw.postalCode === "string" && raw.postalCode.trim()
        ? raw.postalCode.trim()
        : null,
    city:
      typeof raw.city === "string" && raw.city.trim()
        ? raw.city.trim()
        : null,
    state:
      typeof raw.state === "string" && raw.state.trim()
        ? raw.state.trim()
        : null,
    region: normalizeRegion(raw.region),
    confidence: confidence as GeocodingConfidence,
  };
};

export const searchAddress = async (
  query: string
): Promise<GeocodingSearchResult> => {
  try {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      return { ok: true, suggestions: [] };
    }

    const normalizedKey = normalizeQuery(trimmedQuery);
    const cached = cache.get(normalizedKey);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      return { ok: true, suggestions: cached.suggestions };
    }

    const res = await unauthenticatedApi.get(AUTOCOMPLETE_PATH, {
      params: { query: trimmedQuery },
      validateStatus: (status) => status >= 200 && status < 300,
    });

    if (!Array.isArray(res.data)) {
      return { ok: false, kind: "invalid_response", status: res.status };
    }

    const suggestions = dedupeSuggestions(
      res.data
      .map((item: any) => normalizeSuggestion(item))
      .filter((item: GeocodingSuggestion | null): item is GeocodingSuggestion =>
        Boolean(item)
      )
    );

    cache.set(normalizedKey, { ts: Date.now(), suggestions });
    if (cache.size > 50) {
      const firstKey = cache.keys().next().value as string | undefined;
      if (firstKey) {
        cache.delete(firstKey);
      }
    }

    return { ok: true, suggestions };
  } catch (err: any) {
    const status = err?.response?.status as number | undefined;
    if (status && (status === 502 || status === 503 || status === 504)) {
      return { ok: false, kind: "bad_gateway", status };
    }
    if (status) {
      return { ok: false, kind: "invalid_response", status };
    }
    if (err?.message) {
      return { ok: false, kind: "network" };
    }
    return { ok: false, kind: "unknown" };
  }
};

export type GeocodingTelemetryEvent = {
  event: string;
  reason?: string | null;
  confidence?: GeocodingConfidence | null;
};

export const logGeocodingEvent = async (
  payload: GeocodingTelemetryEvent
): Promise<void> => {
  try {
    await unauthenticatedApi.post(TELEMETRY_PATH, payload, {
      headers: { "Content-Type": "application/json" },
      validateStatus: (status) => status >= 200 && status < 300,
    });
  } catch {
    // Telemetry must never block the UI.
  }
};
